import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Overlay from '../shared/Overlay';
import {
  editUser,
  editUserImage,
  associateUserPanicButtons,
  removeUserImage,
  getUser,
} from '../../redux/actions/userActions';

import { Helmet } from 'react-helmet-async';
import UserAddEditForm from './addEditForm/UserAddEditForm';
import UserStaffProfileOptions from './addEditForm/UserStaffProfileOptions';
import UserCheckinPreferencesForm from './addEditForm/UserCheckinPreferencesForm';
import {
  editUserStaffPreferences,
  addNewUserStaffPreferences,
  editUserCategoryDisplayItems,
  editAlertGroupPermissions,
} from '../../redux/actions/userStaffPreferencesActions';
import {
  editUserCheckinProfile,
  createNewUserCheckinProfile,
} from '../../redux/actions/userCheckinProfileActions';
import { removeAssociatedUserPanicButton } from '../../redux/actions/userActions';
import AddEditAlertEmailForm from '../devices/alert/AddEditAlertEmailForm';
import AddEditAlertPhoneNumberForm from '../devices/alert/AddEditAlertPhoneNumberForm';
import { createAssociateAlertDevice } from '../../redux/actions/alertDeviceActions';
import ProgressIndicator from '../shared/progressIndicator/ProgressIndicator';
import { progressStepStatuses } from '../shared/progressIndicator/ProgressIndicatorItem';
import AddEditUserImageForm from './addEditForm/AddEditUserImageForm';
import UserPanicButtonsForm from './addEditForm/UserPanicButtonsForm';
import CategoryDisplayForm from './addEditForm/CategoryDisplayForm';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';
import AssociateAlertDeviceForm from '../devices/alert/AssociateAlertDeviceForm';
import AssociateAlarmDeviceForm from '../devices/alarm/AssociateAlarmDeviceForm';
import { editDeviceAssociation } from '../../redux/actions/deviceActions';
import AssociateUnitsToUserForm from './addEditForm/AssociateUnitsToUserForm';
import { associateUserUnits } from '../../redux/actions/userUnitActions';
import { loadUnit } from '../../redux/actions/unitActions';
import AlertGroupForm from './addEditForm/AlertGroupForm';
import AddEditMassNotificationForm from '../devices/alert/AddEditMassNotificationForm';
import { createAssociateMassNotificationDevice } from '../../redux/actions/alertDeviceActions';
import { noPermissionText } from '../shared/SharedInternationalizations';

/**
 * Wrapper component for editing a user
 */
export function UnconnectedUserEdit(props) {
  const {
    user,
    panicButtons,
    alarmDeviceTypes,
    categoryDisplay,
    access_level,
    currentUserId,
    currentUserPreferences,
  } = props;
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const { state, search } = location;
  const previousRef = useRef((state && state.previous) || '/users/');
  const previous = previousRef.current;

  useEffect(() => {
    if (user.active_unit) {
      props.dispatch(loadUnit(user.active_unit));
      props.dispatch(getUser(user.id));
    }
  }, []); // eslint-disable-line

  // Get the search params for the url to determine what edit interface should be displayed for the form edit queue
  const searchParams = new URLSearchParams(search);
  const mode = searchParams.get('mode');
  const next = searchParams.get('next');
  const prev = searchParams.get('past');

  // Helper function to get the permission feature for each mode
  function getFeatureForMode(mode) {
    switch (mode) {
      case 'checkInPreferences':
        return permissions.static.users.editCheckinExclusions;
      case 'staffOptions':
        return permissions.static.users.editSaraPreferences;
      case 'createCheckInPreferences':
        return permissions.static.users.addUserCheckinProfile;
      case 'createStaffOptions':
        return permissions.static.users.addSaraPreferences;
      case 'createAlertPhone':
      case 'createAlertEmail':
        return permissions.static.devices.alert.addAlertDevice;
      case 'userImageEdit':
        return permissions.static.users.editUser;
      case 'panicButtons':
        return permissions.static.users.editUserButtons;
      case 'categoryEdit':
        return permissions.static.users.editUserCategoryOrder;
      case 'alertGroupEdit':
        return permissions.static.users.editUserAlertGroups;
      case 'associateAlarmDevice':
      case 'associateAlertDevice':
        return permissions.static.devices.editDeviceAssociation;
      case 'associateUnits':
        return permissions.static.users.editUser;
      default:
        return permissions.static.users.editUser;
    }
  }

  // Check if user has permission for the current mode
  function hasPermission() {
    const feature = getFeatureForMode(mode);
    if (feature) {
      // Check if feature requires a preference (but skip for User Admins - level 10)
      if (feature.requiresPreference && access_level !== 10) {
        const hasPreference = currentUserPreferences?.[feature.requiresPreference] === true;
        
        // If feature requires preference and user has it, grant access based on currentUser array
        if (hasPreference && feature.currentUser && feature.currentUser.includes(access_level)) {
          return true;
        }
        
        // Must have the preference enabled to proceed
        if (!hasPreference) {
          return false;
        }
      }
      
      // Original access level checks
      if (feature.permissions.includes(access_level)) {
        return true;
      } else if (
        currentUserId === user.id &&
        feature.currentUser &&
        feature.currentUser.includes(access_level)
      ) {
        return true;
      }
    }
    return false;
  }

  // If user doesn't have permission, show only the no-permission message without overlay
  if (!hasPermission()) {
    return (
      <div>
        <h1 data-test="no-permission">{noPermissionText}</h1>
      </div>
    );
  }

  let nextSteps = [];

  if (next) {
    nextSteps = next.split(',');
  }
  let prevSteps = [];
  if (prev) {
    prevSteps = prev.split(',');
  }

  const formSteps = {
    type: 'users',
    nextSteps,
    prevSteps: [...prevSteps, mode],
    id: user.id,
    previous,
  };

  // set up the display for the progress display bar
  function progressDisplay() {
    if (prevSteps.length > 0 || nextSteps.length > 0) {
      const prevStepsArr = prevSteps.map((item, index) => {
        if (index === 0) {
          return {
            title: item,
            first: true,
            status: progressStepStatuses.complete,
          };
        } else {
          return { title: item, status: progressStepStatuses.complete };
        }
      });
      const nextStepsArr = nextSteps.map((item, index) => {
        if (index === nextSteps.length - 1) {
          return { title: item, last: true };
        } else {
          return { title: item };
        }
      });

      let currentFirst = false;
      let currentLast = false;
      if (nextSteps.length === 0) {
        currentLast = true;
      }
      if (prevSteps.length === 0) {
        currentFirst = true;
      }

      const steps = [
        ...prevStepsArr,
        {
          status: progressStepStatuses.active,
          title: mode,
          first: currentFirst,
          last: currentLast,
        },
        ...nextStepsArr,
      ];

      return (
        <React.Fragment>
          <ProgressIndicator steps={steps} data-test="progress-bar" />
          <hr />
        </React.Fragment>
      );
    } else {
      return '';
    }
  }

  // sets which edit interface is visible
  function editDisplay() {
    switch (mode) {
      case 'checkInPreferences':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editCheckinExclusions}
            permissionMessage={true}
            userToEdit={user.id}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editCheckinTitle"
                defaultMessage="Edit User Check-in Profile: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UserCheckinPreferencesForm
                onSubmit={editCheckinSubmit}
                previous={previous}
                data-test="edit-checkin-preferences-form"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );

      case 'staffOptions':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editSaraPreferences}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editStaffOptionsTitle"
                defaultMessage="Edit User Staff Preferences: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UserStaffProfileOptions
                onSubmit={editStaffSubmit}
                previous={previous}
                data-test="edit-staff-profile-form"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'createCheckInPreferences':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.addUserCheckinProfile}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.addCheckinTitle"
                defaultMessage="Create User Check-in Profile: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UserCheckinPreferencesForm
                onSubmit={createCheckinSubmit}
                previous={previous}
                data-test="create-checkin-preferences-form"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );

      case 'createStaffOptions':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.addSaraPreferences}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.addStaffOptionsTitle"
                defaultMessage="Create User Staff Preferences: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UserStaffProfileOptions
                onSubmit={createStaffSubmit}
                previous={previous}
                data-test="create-staff-profile-form"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );

      case 'createAlertPhone':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.alert.addAlertDevice}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.createAlertPhoneTitle"
                defaultMessage="Create User Alert Phone for: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AddEditAlertPhoneNumberForm
                data-test="create-alert-phone-form"
                previous={previous}
                deviceDetails={{
                  phone_number: user.phone,
                  user: user.id,
                  alert_device_type: user.preferences ? 3 : 24,
                }}
                onSubmit={createDeviceSubmit}
              />
            </React.Fragment>
          </AccessControl>
        );

      case 'createAlertEmail':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.alert.addAlertDevice}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.createAlertEmailTitle"
                defaultMessage="Create User Alert Email for: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AddEditAlertEmailForm
                data-test="create-alert-email-form"
                previous={previous}
                deviceDetails={{
                  email_address: user.email,
                  alert_device_type: user.preferences ? 4 : 25,
                  user: user.id,
                }}
                onSubmit={createDeviceSubmit}
              />
            </React.Fragment>
          </AccessControl>
        );
     case 'createMassnotification':
      const originalFormData = location.state?.formData || {};


      const massNotificationDeviceDetails = {
        user: user.id,
        alert_device_type: 54,
        primary_phone: originalFormData['phone'] || '',
        work_phone: originalFormData['Mass notification work'] || '',
        text_phone: originalFormData['Mass notification Text'] || '',
        secondary_phone: originalFormData['Mass notification Secondary Phone'] || '',
        work_email: originalFormData['work email'] || '',
        personal_email: originalFormData['personal email'] || '',
        secondary_email: originalFormData['secondary email'] || '',
        alert_description: '',
        notes: 'created during user creation',
      };

      // console.log('UserEdit - createMassnotification case');
      // console.log('UserEdit - user object:', user);
      // console.log('UserEdit - massNotificationDeviceDetails:', massNotificationDeviceDetails);

      return (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alert.addAlertDevice}
          permissionMessage={true}>
          <React.Fragment>
            <FormattedMessage
              id="userEdit.createMassnotificationTitle"
              defaultMessage="Create Mass Notification Device for: {userName}"
              description="The title for the page to create a mass notification device"
              data-test="form-header"
              values={{
                userName: userDisplayName,
              }}>
              {(txt) => (
                <h1
                  className="no-margin text-deep-blue text-center"
                  data-test="form-header-text">
                  {txt}
                </h1>
              )}
            </FormattedMessage>
            <hr />
            <AddEditMassNotificationForm
              data-test="create-mass-notification-form"
              previous={previous}
              deviceDetails={massNotificationDeviceDetails}
              onSubmit={createMassnotificationSubmit} 
            />
          </React.Fragment>
        </AccessControl>
      );
      case 'userImageEdit':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editUserImageTitle"
                defaultMessage="Edit User Image: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1 className="no-margin" data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>

              <hr />

              <AddEditUserImageForm
                onSubmit={userImageSubmit}
                previous={previous}
                data-test="edit-user-image"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'panicButtons':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUserButtons}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editUserPanicButtonsTitle"
                defaultMessage="Edit User Panic Buttons: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1 className="no-margin" data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>

              <hr />

              <UserPanicButtonsForm
                onSubmit={userPanicButtonsSubmit}
                previous={previous}
                data-test="edit-user-panic-buttons"
                user={user}
                buttons={panicButtons}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'categoryEdit':
        const alarmDeviceTypesArr = Object.values(alarmDeviceTypes);
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUserCategoryOrder}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editCategoryDisplayTitle"
                defaultMessage="Edit Category Display: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1 className="no-margin" data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>

              <hr />

              <CategoryDisplayForm
                onSubmit={categorySubmit}
                previous={previous}
                data-test="edit-category-display"
                user={user}
                categoriesArr={alarmDeviceTypesArr}
                categories={alarmDeviceTypes}
                categoryDisplay={categoryDisplay}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'alertGroupEdit':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUserAlertGroups}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editAlertGroupsTitle"
                defaultMessage="Edit Alert Group Permissions: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1 className="no-margin" data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>

              <hr />

              <AlertGroupForm
                onSubmit={alertGroupSubmit}
                previous={previous}
                data-test="edit-alert-group-display"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateAlarmDevice':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.editDeviceAssociation}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.associateAlarmDevicesText"
                defaultMessage="Edit monitored devices for: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateAlarmDeviceForm
                onSubmit={associateDeviceSubmit}
                previous={previous}
                data-test="associate-alarm-device-form"
                associateId={user.id}
                type="user"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateAlertDevice':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.editDeviceAssociation}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.associateAlertDevicesText"
                defaultMessage="Edit alert devices for: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateAlertDeviceForm
                onSubmit={associateDeviceSubmit}
                previous={previous}
                data-test="associate-alert-device-form"
                associateId={user.id}
                type="user"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateUnits':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.associateUnitsText"
                defaultMessage="Associate units to: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateUnitsToUserForm
                onSubmit={associateUserUnitsSubmit}
                previous={previous}
                data-test="associate-units-to-user-form"
                user={user.id}
                units={user.units}
                type="user"
              />
            </React.Fragment>
          </AccessControl>
        );

      default:
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            userToEdit={user.id}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="userEdit.editUserTitle"
                defaultMessage="Edit User: {userName}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  userName: userDisplayName,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>

              <hr />
              <UserAddEditForm
                onSubmit={editUserSubmit}
                previous={previous}
                data-test="edit-user-form"
                user={user}
              />
            </React.Fragment>
          </AccessControl>
        );
    }
  }

  /**
   * Function for submitting the edited user information
   * @param {object} values
   * @description - Because React Final Form will return nothing if a field is empty, this also means that a field that formerly was filled but now blank will not return a blank value. This is specifically for text inputs because checkboxes will at least return a "false" value. So the series of `if` statements are to check and see if there had been content in a field prior to editing and if the field isn't returned then it will add an empty string to the values object so that it will correctly update the API with blank information
   */
  const editUserSubmit = (values) => {
    props.dispatch(editUser(values, navigate, previous));
  };

  function editStaffSubmit(values) {
    props.dispatch(
      editUserStaffPreferences(values, navigate, previous, formSteps)
    );
  }

  function editCheckinSubmit(values) {
    props.dispatch(
      editUserCheckinProfile(values, navigate, previous, formSteps)
    );
  }

  function createStaffSubmit(values) {
    props.dispatch(
      addNewUserStaffPreferences(values, navigate, previous, formSteps)
    );
  }

  function createCheckinSubmit(values) {
    let data = { ...values };
    if (!values.checkinExclude) {
      delete data.exclusion_start_time;
      delete data.exclusion_end_time;
    }
    props.dispatch(
      createNewUserCheckinProfile(data, navigate, previous, formSteps)
    );
  }

  function createDeviceSubmit(values) {
    props.dispatch(
      createAssociateAlertDevice(values, navigate, previous, formSteps)
    );
  }

  const createMassnotificationSubmit = (values) => {
  // console.log('createMassnotificationSubmit - values:', values);
  
  const massNotificationValues = {
    ...values,
    user: values.user || user.id
  };
  
  props.dispatch(
    createAssociateMassNotificationDevice(massNotificationValues, navigate, previous, formSteps)
  );
};

  function userPanicButtonsSubmit(values) {
    // compare the buttons that were assigned initially to the ones that are being submitted and if there are any that were there initially
    // but no longer there, they've been removed and are added to the removed buttons array
    const removedButtons = values.initialButtons.filter(
      (d) => !values.buttons.includes(d)
    );
    // panic buttons need to be associated one at a time so this iterates over the selected buttons and sends off an api call for each
    values.buttons.map((button) => {
      if (!values.initialButtons.includes(button)) {
        const body = {
          button_id: panicButtons[button].button_id,
          username: user.username,
          userId: user.id,
        };

        props.dispatch(associateUserPanicButtons(body, navigate, previous));
      }
      return '';
    });
    // check to see if there are any buttons that have been removed and send api call for those
    if (removedButtons.length > 0) {
      removedButtons.map((button) => {
        const buttonArr = user.userbuttons.filter(
          (b) => b.button_id === button
        );

        const buttonId = {
          button: buttonArr[0].id,
          buttonId: buttonArr[0].button_id,
          userId: user.id,
        };
        props.dispatch(
          removeAssociatedUserPanicButton(buttonId, navigate, previous)
        );
        return '';
      });
    }
  }

  function categorySubmit(values) {
    // find categories already assigned to this user
    const usersCategories = Object.values(categoryDisplay).filter(
      (c) => c.username === user.username
    );
    // filter out categories that have been removed
    const removedCategories = values.initialCategories.filter(
      (e) => !values.categories.includes(e)
    );
    // get the id of the categories that have been removed
    const removed = removedCategories.map((c) => {
      const originalCategory = usersCategories.filter(
        (item) => item.alarm_device_type === c
      );
      return originalCategory[0].id;
    });

    const addEdit = values.categories.map((c, index) => {
      const info = {};

      if (values.initialCategories.includes(c)) {
        const originalCategory = usersCategories.filter(
          (item) => item.alarm_device_type === c
        );
        info.id = originalCategory[0].id;
        if (originalCategory[0].category_order === index) {
          info.edit = false;
        } else {
          info.edit = true;
        }
      } else {
        info.new = true;
      }
      info.category_order = index;
      info.alarm_device_type = c;
      return info;
    });

    const data = {
      addEdit,
      removed,
      user: user.id,
      username: user.username,
    };

    props.dispatch(
      editUserCategoryDisplayItems(data, navigate, previous, formSteps)
    );
  }

  function associateDeviceSubmit(values) {
    const data = {
      values,
      type: 'user',
      id: user.id,
    };

    props.dispatch(editDeviceAssociation(data, navigate, previous, formSteps));
  }

  async function userImageSubmit(image) {
    const meta = {
      previous: previous,
      navigate: navigate,
      formSteps: formSteps,
      noCacheImage: true, // this is to prevent the browser from caching the image so that when it's updated it will show the new image instead of the cached one
    };

    // since it's possible to remove the user image and submit the form to delete the user's profile image, there needs to be a check to see if there is or isn't an image so that the form will be submitted correctly
    // if there is an image, the path is different from if there isn't an image
    if (image) {
      const body = {
        user: user,
        image: image,
      };
      props.dispatch(editUserImage(body, meta));
    } else {
      const body = {
        user: user,
      };
      props.dispatch(removeUserImage(body, meta));
    }
  }

  function alertGroupSubmit(values) {
    props.dispatch(editAlertGroupPermissions(values, navigate));
  }

  const associateUserUnitsSubmit = (values) => {
    props.dispatch(associateUserUnits(values, navigate, previous, formSteps));
  };

  // This puts together the display name for the user. It's put together using template literals and checks for the existence of different parts of the name and displays them if they exist. If they don't exist, they would show as "undefined" so checking to make sure they exist before displaying them is necessary
  const userDisplayName = `${user.title ? user.title : ''} ${user.first_name} ${
    user.preferred_name ? `"${user.preferred_name}"` : ''
  } ${user.middle_name ? user.middle_name : ''} ${user.last_name} ${
    user.suffix ? user.suffix : ''
  }`;

  return (
    <Overlay rootUrl={previous} canClose={true} data-test="edit-user-overlay">
      <FormattedMessage
        id="userEdit.browserTabInfo"
        defaultMessage="Users | Editing {userName}"
        description="Done button"
        values={{
          userName: userDisplayName,
        }}>
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="content-container content-container--column">
        {progressDisplay()}

        {editDisplay()}
      </div>
    </Overlay>
  );
}

const mapStateToProps = (state, ownProps) => {
  const id = ownProps.userId;
  const user = state.users.users[id] || {};

  return {
    user,
    panicButtons: state.users.panicButtons,
    categoryDisplay: state.users.categoryDisplay,
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    access_level: state.currentUser.preferences.access_level,
    currentUserId: state.currentUser.id,
    currentUserPreferences: state.currentUser.preferences,
  };
};

// Wrapper component to get the user ID from params and pass it to the connected component
function UserEditWrapper() {
  const { id } = useParams();
  const ConnectedUserEdit = connect(mapStateToProps)(UnconnectedUserEdit);
  return <ConnectedUserEdit userId={id} />;
}

export default UserEditWrapper;
